import React from 'react';
import { X, CaretLeft, CaretRight } from 'phosphor-react';
import { SkinPhoto } from '../../types/entities';
import { formatDate } from '../../utils/date';
import usePresence from '../../use/usePresence';
import LazyImage from '../ui/LazyImage';

interface SkinPhotoCompareModalProps {
  aberto: boolean;
  photos: SkinPhoto[];
  onClose: () => void;
}

export default function SkinPhotoCompareModal({ aberto, photos, onClose }: SkinPhotoCompareModalProps) {
  const [leftIdx, setLeftIdx] = React.useState(0);
  const [rightIdx, setRightIdx] = React.useState(photos.length - 1);
  const { montado, estado } = usePresence(aberto, 180);

  // O componente deixou de montar e desmontar a cada abertura (a saída precisa
  // dele vivo), então o par de fotos volta ao padrão à mão a cada vez que abre.
  React.useEffect(() => {
    if (aberto) {
      setLeftIdx(0);
      setRightIdx(photos.length - 1);
    }
  }, [aberto, photos.length]);

  if (!montado || photos.length < 2) return null;

  const left = photos[leftIdx];
  const right = photos[rightIdx];

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-50 bg-black/90 flex flex-col data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out data-[state=closed]:pointer-events-none"
      onClick={onClose}
    >
      <div
        className="flex-1 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gray-900 px-5 pt-12 pb-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-white font-bold text-lg">Antes / Depois</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Esquerda */}
          <div className="flex-1 flex flex-col border-r border-white/10">
            <div className="flex-1 relative bg-black overflow-hidden">
              <LazyImage
                src={left.photoUrl}
                alt={formatDate(left.photoDate)}
                eager
                className="w-full h-full"
                imgClassName="w-full h-full object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                <p className="text-white text-xs font-semibold">{formatDate(left.photoDate)}</p>
                {left.notes && (
                  <p className="text-white/70 text-[11px] truncate">{left.notes}</p>
                )}
              </div>
            </div>
            <div className="bg-gray-900 px-3 py-2 flex items-center gap-2">
              <button
                onClick={() => setLeftIdx((i) => Math.max(0, i - 1))}
                disabled={leftIdx === 0}
                className="p-1.5 rounded-lg text-white/60 hover:text-white disabled:opacity-30 transition-colors"
              >
                <CaretLeft className="w-4 h-4" />
              </button>
              <p className="flex-1 text-center text-white/70 text-xs font-medium">
                {leftIdx + 1} / {photos.length}
              </p>
              <button
                onClick={() => setLeftIdx((i) => Math.min(photos.length - 1, i + 1))}
                disabled={leftIdx === photos.length - 1}
                className="p-1.5 rounded-lg text-white/60 hover:text-white disabled:opacity-30 transition-colors"
              >
                <CaretRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Direita */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 relative bg-black overflow-hidden">
              <LazyImage
                src={right.photoUrl}
                alt={formatDate(right.photoDate)}
                eager
                className="w-full h-full"
                imgClassName="w-full h-full object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                <p className="text-white text-xs font-semibold">{formatDate(right.photoDate)}</p>
                {right.notes && (
                  <p className="text-white/70 text-[11px] truncate">{right.notes}</p>
                )}
              </div>
            </div>
            <div className="bg-gray-900 px-3 py-2 flex items-center gap-2">
              <button
                onClick={() => setRightIdx((i) => Math.max(0, i - 1))}
                disabled={rightIdx === 0}
                className="p-1.5 rounded-lg text-white/60 hover:text-white disabled:opacity-30 transition-colors"
              >
                <CaretLeft className="w-4 h-4" />
              </button>
              <p className="flex-1 text-center text-white/70 text-xs font-medium">
                {rightIdx + 1} / {photos.length}
              </p>
              <button
                onClick={() => setRightIdx((i) => Math.min(photos.length - 1, i + 1))}
                disabled={rightIdx === photos.length - 1}
                className="p-1.5 rounded-lg text-white/60 hover:text-white disabled:opacity-30 transition-colors"
              >
                <CaretRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
